/**
 * The kitchen's capacity, as a model the merchant can argue with.
 *
 * Little's Law is an identity — `L = λW`, work in the system equals the rate it
 * arrives times the time it spends there — and rearranged it answers the one
 * question a busy kitchen actually asks: *can I clear my busiest hour?*
 *
 * ```
 * λ_max = c / S        the most this kitchen can clear per unit time
 * ρ     = λ / λ_max    how loaded it is; at 1 the queue grows without bound
 * ```
 *
 * `c` is how many orders the kitchen works on at once (`kitchen_capacity`), `S`
 * the time a station is busy on one order, `λ` the arrival rate.
 *
 * The wait term is Allen–Cunneen, the standard approximation for a multi-server
 * queue with general service times:
 *
 * ```
 * Wq ≈ (Ca² + Cs²)/2 · ρ^(√(2(c+1)) − 1) / (c·(1 − ρ)) · S
 * ```
 *
 * At `c = 1` it reduces exactly to the Pollaczek–Khinchine form this product's
 * design note starts from, `ρ/(1−ρ) · E[S²]/(2·E[S])`, which is a useful thing
 * for the two to agree on.
 *
 * Two honest caveats, both of which the calling page is expected to carry:
 *
 * - **It is a model, not a measurement.** Its job is direction and magnitude —
 *   "another station buys you this much" — not a promised minute. The measured
 *   split from `cook_rounds` is the truth; this is the what-if.
 * - **It assumes a steady state.** A dinner rush is transient by definition, so
 *   the number is the average the kitchen settles at, not the worst minute.
 *
 * Named `Cs` for the squared coefficient of variation of service time,
 * `Var(S)/E[S]²`, and `Ca` for the same of arrivals — 1 for a Poisson stream,
 * which is the standing assumption for a shop.
 */

/** What the calculator is asked, all in consistent units. */
export interface CapacityInput {
  /** Orders the kitchen works on at once — `kitchen_capacity`. At least 1. */
  ordersAtOnce: number;
  /** Seconds a station is busy on one order. Greater than zero. */
  cookSeconds: number;
  /** Orders arriving per hour. */
  arrivalsPerHour: number;
  /** Squared coefficient of variation of cook time. 1 when the log has none. */
  serviceVariability: number;
  /** Squared coefficient of variation of arrivals. Poisson is 1. */
  arrivalVariability: number;
}

/** How loaded the kitchen is, as a word the page can print. */
export type CapacityVerdict = 'calm' | 'busy' | 'critical' | 'over';

export interface CapacityResult {
  /** The most the kitchen can clear in an hour. */
  clearablePerHour: number;
  /** Utilisation: arrivals over what it can clear. 1 or more is unsustainable. */
  load: number;
  /**
   * Average wait before the heat, in seconds, or `null` when the kitchen cannot
   * keep up — which is not "a long wait" but "a wait that grows without bound".
   */
  waitSeconds: number | null;
  /** Wait plus cook, or `null` on the same terms. */
  totalSeconds: number | null;
  verdict: CapacityVerdict;
}

/** Where each verdict starts, in utilisation. */
const CRITICAL_LOAD = 0.85;
const BUSY_LOAD = 0.6;

/**
 * Works out what the kitchen can clear and how long an order waits.
 *
 * @param input Orders at a time, cook time, arrival rate and the two variabilities.
 * @returns The capacity, the load, the wait, and a word for it.
 */
export function capacityModel(input: CapacityInput): CapacityResult {
  const ordersAtOnce = Math.max(1, input.ordersAtOnce);
  const cookSeconds = Math.max(1, input.cookSeconds);
  const arrivalsPerHour = Math.max(0, input.arrivalsPerHour);

  const clearablePerHour = (ordersAtOnce * 3600) / cookSeconds;
  // Offered load in Erlangs: the arrival rate times the time each one occupies
  // one of the kitchen's slots. Dividing by the slots is the utilisation.
  const offeredLoad = (arrivalsPerHour / 3600) * cookSeconds;
  const load = offeredLoad / ordersAtOnce;

  const verdict: CapacityVerdict =
    load >= 1 ? 'over' : load >= CRITICAL_LOAD ? 'critical' : load >= BUSY_LOAD ? 'busy' : 'calm';

  if (load >= 1) {
    return {
      clearablePerHour,
      load,
      waitSeconds: null,
      totalSeconds: null,
      verdict,
    };
  }

  const variability = (input.arrivalVariability + input.serviceVariability) / 2;
  const exponent = Math.sqrt(2 * (ordersAtOnce + 1)) - 1;
  const congestion = Math.pow(load, exponent) / (ordersAtOnce * (1 - load));
  const waitSeconds = variability * congestion * cookSeconds;

  return {
    clearablePerHour,
    load,
    waitSeconds,
    totalSeconds: waitSeconds + cookSeconds,
    verdict,
  };
}

/** What one round window does at a given arrival rate. */
export interface RoundWindowResult {
  /** Orders expected to gather in the window. */
  batchSize: number;
  /**
   * The average time an order spends waiting for the window to close.
   *
   * Uniform arrivals over a window of `W` means the first order waits the whole
   * of it and the last waits none, so the mean is half. This is the batching
   * penalty the design note names, and it is why a shorter window trades a
   * smaller batch for a shorter wait.
   */
  batchWaitSeconds: number;
}

/**
 * What one round window costs, at a given arrival rate.
 *
 * @param windowSeconds How long a round stays open.
 * @param arrivalsPerHour Orders arriving per hour.
 * @returns The expected batch size and the average wait it imposes.
 */
export function roundWindowModel(windowSeconds: number, arrivalsPerHour: number): RoundWindowResult {
  const window = Math.max(0, windowSeconds);
  return {
    batchSize: (Math.max(0, arrivalsPerHour) * window) / 3600,
    batchWaitSeconds: window / 2,
  };
}

/**
 * The squared coefficient of variation of a sample, `Var/S²`.
 *
 * @param meanSeconds The mean.
 * @param secondMomentSeconds2 The second moment **about zero**, `E[S²]`, which is
 *   the form the database returns.
 * @returns The squared coefficient of variation, never negative.
 */
export function squaredVariation(meanSeconds: number, secondMomentSeconds2: number): number {
  if (meanSeconds <= 0 || secondMomentSeconds2 <= 0) return 1;
  const variance = secondMomentSeconds2 - meanSeconds * meanSeconds;
  return Math.max(0, variance / (meanSeconds * meanSeconds));
}

/** Why the next round should start now, or why it should wait a little longer. */
export type CutoffReason =
  /** Nothing is waiting. There is no round to cut. */
  | 'nothing'
  /** Enough orders to make the batch this shop normally cooks. */
  | 'enough'
  /** The oldest order has waited as long as the shop's own window would allow. */
  | 'waited'
  /** Not enough to fill a round and nobody has waited too long yet. */
  | 'filling';

/** What the cutoff rule is asked. */
export interface CutoffInput {
  /** Orders waiting to be cooked: accepted, not yet ready, not booked. */
  waiting: number;
  /** How long the oldest of them has waited, in seconds. */
  oldestWaitSeconds: number;
  /**
   * What this shop cooks at a time, from its own log. A round that reaches this
   * is worth starting rather than holding open for more.
   */
  targetBatch: number;
  /**
   * The longest wait the shop's round window already tolerates — one full window,
   * because an order placed as a round opens waits the whole of it.
   */
  maxWaitSeconds: number;
}

export interface CutoffResult {
  start: boolean;
  reason: CutoffReason;
  /**
   * How much longer to hold, when the answer is to hold. Zero when starting,
   * and `null` when there is nothing to wait for.
   */
  waitMoreSeconds: number | null;
}

/**
 * Whether to cut the next round now, and why.
 *
 * The dynamic half of what the fixed round window does by clock. Two answers are
 * worth acting on, and they are the two ways a fixed window is wrong: a round
 * that has already filled should not wait for the window to close, and an order
 * that has waited a full window should not wait for a companion that may never
 * come.
 *
 * Deliberately *not* gated on a free station. The dynamic rule in the design
 * note gates on one, but capacity in use cannot be read reliably from this
 * schema — `preparing` means "started" for a shop that presses it and "accepted"
 * for a shop with auto-accept on — so a gate built on it would tell those shops
 * their kitchen is permanently full. The size and wait triggers are the two
 * things that are trustworthy, and this is advice rather than automation: the
 * cook knows whether the wok is free.
 *
 * @param input The waiting count, the oldest wait, and the two thresholds.
 * @returns Whether to start, why, and how long to hold if not.
 */
export function roundCutoff(input: CutoffInput): CutoffResult {
  if (input.waiting <= 0) {
    return { start: false, reason: 'nothing', waitMoreSeconds: null };
  }

  if (input.waiting >= Math.max(1, input.targetBatch)) {
    return { start: true, reason: 'enough', waitMoreSeconds: 0 };
  }

  if (input.oldestWaitSeconds >= input.maxWaitSeconds) {
    return { start: true, reason: 'waited', waitMoreSeconds: 0 };
  }

  return {
    start: false,
    reason: 'filling',
    waitMoreSeconds: Math.max(0, input.maxWaitSeconds - input.oldestWaitSeconds),
  };
}
